<template>
    <div class="p-4 bg-white shadow-md">
        <h2 class="text-xl font-semibold text-gray-800 flex items-center mb-4">
            <span class="text-2xl mr-2">📊</span>
            <span>Retention Rates by Program (Year-on-Year)</span>
        </h2>

        <!-- Admission Year Filter -->
        <div class="mb-2">
            <label class="text-sm font-medium text-gray-700 mr-2">Admission Year:</label>
            <select v-model="selectedYear" class="border outline-none text-sm px-2 py-1">
                <option value="">All</option>
                <option v-for="year in admissionYears" :key="year" :value="year">{{ year }}</option>
            </select>
        </div>

        <template v-if="hasData">
            <div v-for="(chunk, index) in chunks" :key="index" class="">
                <apexchart type="bar" height="700" :options="getChartOptions(chunk)" :series="getSeries(chunk)" />
            </div>
        </template>

        <div v-else class="text-gray-500 text-center py-16">
            <div class="text-4xl mb-2">⚠️</div>
            <div>There is no data to show</div>
        </div>
    </div>
</template>

<script>
export default {
    name: "RetentionRatesYearOnYearByProgram",
    props: {
        data: {
            type: Array,
            default: () => [],
        },
        chunkSize: {
            type: Number,
            default: 100, // split into groups of 10 (gives 2 charts for 20 programs)
        },
        clearInnerFilters: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedYear: "",
            admissionYears: [
                "2015-2016",
                "2016-2017",
                "2017-2018",
                "2018-2019",
                "2019-2020",
                "2020-2021",
                "2021-2022",
                "2022-2023",
                "2023-2024",
                "2024-2025",
            ],
        };
    },
    watch: {
        selectedYear(newVal) {
            this.$emit('fetch-retention-rates-by-program', newVal);
        },
        clearInnerFilters(newVal) {
            if (newVal) {
                this.selectedYear = "";
            }
        },
    },
    computed: {
        hasData() {
            return Array.isArray(this.data) && this.data.length > 0;
        },
        chunks() {
            const arr = [];
            for (let i = 0; i < this.data.length; i += this.chunkSize) {
                arr.push(this.data.slice(i, i + this.chunkSize));
            }
            return arr;
        },
    },
    methods: {
        getSeries(chunk) {
            const safe = (v) => (v == null ? 0 : Number(v));

            return [
                {
                    name: "Year 1",
                    data: chunk.map((d) => safe(d.year1_retention_rate)),
                },
                {
                    name: "Year 2",
                    data: chunk.map((d) => safe(d.year2_retention_rate)),
                },
                {
                    name: "Year 3",
                    data: chunk.map((d) => safe(d.year3_retention_rate)),
                },
                {
                    name: "Year 4",
                    data: chunk.map((d) => safe(d.year4_retention_rate)),
                },
                {
                    name: "Year 5",
                    data: chunk.map((d) => safe(d.year5_retention_rate)),
                },
            ];
        },
        getChartOptions(chunk) {
            return {
                chart: {
                    type: "bar",
                    stacked: true,
                    height: 500,
                    toolbar: { show: false },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: "60%",
                        dataLabels: {
                            total: {
                                enabled: false,
                                offsetX: 0,
                                style: {
                                    fontSize: "12px",
                                    fontWeight: 700,
                                },
                                formatter: (total) => `${total}%`,
                            },
                        },
                    },
                },
                dataLabels: {
                    enabled: true,
                    formatter: (val) => (val ? `${val}%` : ""),
                    style: { fontSize: "11px", fontWeight: 600 },
                },
                stroke: { width: 1, colors: ["#fff"] },
                xaxis: {
                    labels: {
                        show: false
                    },
                    categories: chunk.map((d) => d.program_name), // y-axis categories
                },
                yaxis: { title: { text: undefined } },
                tooltip: {
                    y: {
                        formatter: (val) => `${Number(val)}%`,
                    },
                },
                fill: { opacity: 1 },
                legend: {
                    position: "top",
                    horizontalAlign: "right",
                    offsetX: 40,
                },
                colors: [
                    "#01B8AA", // teal
                    "#3599B8", // medium blue
                    "#8AD4EB", // light blue
                    "#A66999", // purple
                    "#5F6B6D", // grayish blue
                    "#374649", // dark gray
                    "#FD625E", // red
                    "#F2C80F", // yellow
                    "#FE9666", // orange
                    "#DFBFBF"  // soft pink/gray
                ],
            };
        },
    },
};
</script>